import { AxiosError } from 'axios';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Grid } from '../../../app/StyleGuide';
import { PrimaryButton } from '../../../components/Buttons';
import EmptyState from '../../../components/EmptyState';
import ErrorMessage from '../../../components/ErrorMessage';
import { CircleCheck } from '../../../components/FormElements';
import LoadingState from '../../../components/LoadingState';
import windowService from '../../../services/windowService';
import { logger } from '../../../utils/logger';
import { BrandCode } from '../../curriculum';
import { Item } from '../models/datastore';
import { OnboardingTypeConfig } from '../models/onboarding';
import { ContentTitle, ContinueButton, ListItem, ViewContainer } from './OnboardingElements';

const EmptyStateContainer = styled.div`
  text-align: center;

  .empty-state-ui {
    margin: 0;
  }

  button {
    margin-top: ${Grid._4};
  }
`;

const AgeGroupsContainer = styled(ViewContainer)`
  display: flex;
  flex-direction: column;
  align-items: center;
  grid-gap: ${Grid._4};
`;

const hasItem = (arr: Item[], name: string) => arr.some(x => x.name === name);

const AgeGroupItem = ({ name, isSelected, onClick }: { name: string; isSelected: boolean; onClick: () => void }) => (
  <ListItem data-qa-hook="ageGroupItem" onClick={onClick} aria-pressed={isSelected}>
    <h2 data-qa-hook="ageGroupName">{name}</h2>
    <CircleCheck checked={isSelected} />
  </ListItem>
);

type AgeGroupsViewProps = {
  brandCode: BrandCode;
  storedItems?: Item[];
  handler: (items: Item[]) => void;
  getCurriculum: OnboardingTypeConfig['getCurriculum'];
  preselectedAges?: string[];
};

export const AgeGroupsView = ({
  brandCode,
  storedItems = [],
  preselectedAges = [],
  handler,
  getCurriculum,
}: AgeGroupsViewProps) => {
  const [status, setStatus] = useState({ isLoading: true, isError: false });
  const [selected, setSelected] = useState<Item[]>([]);
  const [curriculum, setCurriculum] = useState<Item[]>([]);

  const onItemClick = (item: Item) => {
    setSelected(prev => {
      const items = hasItem(selected, item.name) ? prev.filter(i => i.name !== item.name) : [...prev, item];
      return items.filter(i => !!i);
    });
  };

  const onContinueClick = () => handler(selected);

  useEffect(() => {
    getCurriculum(brandCode)
      .then((curriculumOptions: Item[]) => {
        if (preselectedAges.length) {
          setSelected(curriculumOptions.filter(c => preselectedAges.includes(c.name)));
        } else if (storedItems.length) {
          setSelected(storedItems);
        }

        setCurriculum(curriculumOptions);
        setStatus({ isLoading: false, isError: false });
      })
      .catch((error: AxiosError) => {
        logger.error('Error getting curriculum for onboarding', error);
        setStatus({ isLoading: false, isError: true });
      });
  }, [brandCode, getCurriculum]);

  return status.isLoading ? (
    <LoadingState />
  ) : status.isError ? (
    <ErrorMessage />
  ) : curriculum.length === 0 ? (
    <EmptyStateContainer>
      <EmptyState title="No Trials Available" description="View our full line of curriculum to get started today" />
      <PrimaryButton onClick={() => windowService.redirectTo('/bible-studies')}>View Curriculum</PrimaryButton>
    </EmptyStateContainer>
  ) : (
    <>
      <ContentTitle>
        <h2>Select Age Group</h2>
        <p>Select one or more curriculum age groups to try out</p>
      </ContentTitle>
      <AgeGroupsContainer data-qa-hook="ageGroupContainer">
        {curriculum.map(({ name, itemNumber }) => (
          <AgeGroupItem
            key={name}
            name={name}
            isSelected={hasItem(selected, name)}
            onClick={() => onItemClick({ name, itemNumber })}
          />
        ))}
      </AgeGroupsContainer>
      <ContinueButton data-qa-hook="continueButton" disabled={selected.length === 0} onClick={onContinueClick}>
        Continue
      </ContinueButton>
    </>
  );
};
